import type { ReactNode } from 'react'
import { formatBytes } from '@bloo-team/api-client'
import { Button } from '@bloo-team/ui'
import { STAGES, hidden, shown, type Loss, type PlantModel, type Stage } from '../lib/plant'
import { FoldButton, Gauge, PriorityGlyph, ReadNote, Share, ageText, type InspectTarget, type RenderLink } from './plant-parts'

/**
 * The plant (docs/fabric-overview.md §The plant): five stage units left to
 * right — Collection · Extraction · Enrichment · Data model · Storage — joined
 * by pipes that carry instruments, with a losses band beneath. ISA-101 rules:
 * grey is normal, colour only where abnormal, shape + colour for priority,
 * dashes move only where data moved today.
 *
 * Props in, no data, no router: `renderLink` turns a check link into the
 * page's Link (plain text for a Viewer), `onInspect` opens the deep dive.
 * Every list is a Fold: the first few rows here, the rest behind Inspect.
 */

export interface ScanControl {
  state: PlantModel['extraction']['scan']['state']
  /** Present when the session may run the search job (Analyst/Engineer). */
  onRun?: () => void
}

export interface PlantProps {
  model: PlantModel
  now: number
  canFollow: boolean
  renderLink: RenderLink
  onInspect: (target: InspectTarget) => void
  scan: ScanControl
}

const STAGE_TITLE = Object.fromEntries(STAGES.map((s) => [s.key, s.title])) as Record<Stage, string>
const STAGE_READS = Object.fromEntries(STAGES.map((s) => [s.key, s.reads])) as Record<Stage, string>

function Unit({ stage, abnormal, off, children }: { stage: Stage; abnormal: boolean; off?: boolean; children: ReactNode }) {
  const id = `plant-${stage}`
  return (
    <section id={id} aria-labelledby={`${id}-title`} className="pl-unit" data-stage={stage} data-abnormal={abnormal || undefined} data-off={off || undefined}>
      <div className="pl-unit-head">
        <h2 id={`${id}-title`} className="pl-unit-title">{STAGE_TITLE[stage]}</h2>
        <span className="hud-unit pl-unit-reads">{STAGE_READS[stage]}</span>
      </div>
      {children}
    </section>
  )
}

/** A pipe between two units, carrying its instruments. Dashes move only when `flow` is true. */
function Pipe({ flow, children }: { flow: boolean; children?: ReactNode }) {
  return (
    <div className="pl-pipe" data-flow={flow ? 'on' : 'off'} aria-hidden="true">
      <span className="pl-pipe-line" />
      {children}
    </div>
  )
}

/**
 * An ISA instrument on a pipe: a ringed tag with a name and a value. Solid ring
 * = wired today; dashed ring = proposed, needs a backend read (BACKEND-ASKS).
 */
function Instrument({ tag, name, value, wired }: { tag: string; name: string; value: string; wired: boolean }) {
  return (
    <span className="pl-inst" data-wired={wired ? 'yes' : 'no'} title={wired ? name : `${name} · proposed · needs a backend read`}>
      <span className="pl-inst-ring">{tag}</span>
      <span className="pl-inst-name">{name}</span>
      <span className="pl-inst-value">{value}</span>
    </span>
  )
}

const STATE_LABEL = { active: 'seen', stale: 'not seen 24h', inactive: 'inactive', decommissioned: 'decom?' } as const
const priorityOf = (state: keyof typeof STATE_LABEL) => (state === 'inactive' ? 'high' : state === 'stale' ? 'medium' : state === 'decommissioned' ? 'info' : null)

export function Plant({ model, now, canFollow, renderLink, onInspect, scan }: PlantProps) {
  const { collection, extraction, dataModel, storage } = model
  const abnormal = (stage: Stage) => model.abnormalStages.includes(stage)
  const flowing = (collection.eventsToday ?? 0) > 0
  const partial = collection.devicesTotal !== null && collection.devicesLoaded < collection.devicesTotal
  const link = (l: Loss, children: ReactNode, label: string) => (canFollow && l.state === 'issue' ? renderLink(l.link, children, label) : <span className="pl-loss-plain">{children}</span>)

  return (
    <div className="pl-plant">
      <div className="pl-stages">

        {/* ---------------------------------------------------- Collection */}
        <Unit stage="collection" abnormal={abnormal('collection')}>
          <ReadNote state={collection.read} what="devices" />
          <div className="pl-unit-row">
            <span className="hud-eyebrow">inlets · {collection.devicesLoaded.toLocaleString()} devices{partial ? ` of ${collection.devicesTotal!.toLocaleString()}` : ''}</span>
            {collection.archivedHidden > 0 ? <span className="hud-unit">{collection.archivedHidden} archived in this browser</span> : null}
          </div>
          <ul className="pl-inlets" aria-label="Devices, problems first">
            {shown(collection.inlets).map((i) => {
              const p = priorityOf(i.state)
              return (
                <li key={i.address} className="pl-inlet" data-state={i.state}>
                  <span className="pl-inlet-flow" data-flow={i.state === 'active' ? 'on' : 'off'} aria-hidden="true" />
                  {p ? <PriorityGlyph priority={p} /> : <span className="pl-glyph" aria-hidden="true" />}
                  <span className="pl-inlet-addr">{i.address}</span>
                  <span className="pl-inlet-meta">{STATE_LABEL[i.state]} {ageText(i.lastSeenMs, now)} · {i.extractor ?? 'no extractor'}</span>
                </li>
              )
            })}
          </ul>
          <FoldButton hidden={hidden(collection.inlets)} noun="devices" onInspect={() => onInspect('devices')} />
          <div className="pl-unit-row pl-unit-gauges">
            <Gauge value={collection.healthy} max={collection.inlets.items.length} label="healthy devices" />
            <span className="pl-counts">
              {collection.inactive > 0 ? <span data-priority="high"><PriorityGlyph priority="high" /> {collection.inactive} inactive</span> : null}
              {collection.stale > 0 ? <span data-priority="medium"><PriorityGlyph priority="medium" /> {collection.stale} stale</span> : null}
              {collection.decommissioned > 0 ? <span data-priority="info"><PriorityGlyph priority="info" /> {collection.decommissioned} decom</span> : null}
            </span>
          </div>
          <span className="hud-eyebrow">streams · share of events today</span>
          <ul className="pl-outlets" aria-label="Streams, problems first">
            {shown(collection.outlets).map((o) => (
              <li key={o.name} className="pl-outlet" data-state={o.state}>
                <span className="pl-outlet-name">{o.name}</span>
                {o.state === 'unmapped' ? <span className="pl-outlet-flag" data-priority="high"><PriorityGlyph priority="high" /> no sources</span>
                  : o.state === 'silent' ? <span className="pl-outlet-flag" data-priority="medium"><PriorityGlyph priority="medium" /> silent</span>
                  : o.state === 'muted' ? <span className="pl-outlet-flag">no events yet</span>
                  : <Share share={o.share} />}
              </li>
            ))}
          </ul>
          <FoldButton hidden={hidden(collection.outlets)} noun="streams" onInspect={() => onInspect('streams')} />
        </Unit>

        <Pipe flow={flowing}>
          <Instrument tag="EV" name="events today" value={collection.eventsToday === null ? '—' : collection.eventsToday.toLocaleString()} wired />
          <Instrument tag="LG" name="ingest lag" value="—" wired={false} />
        </Pipe>

        {/* ---------------------------------------------------- Extraction */}
        <Unit stage="extraction" abnormal={abnormal('extraction')}>
          <ReadNote state={extraction.read} what="extractors" />
          <div className="pl-unit-row">
            <span className="hud-eyebrow">extractors · {extraction.total}</span>
            <span className="hud-unit">{extraction.custom} custom · {extraction.total - extraction.custom} native</span>
          </div>
          <Gauge value={extraction.enabled} max={extraction.total} label="enabled extractors" />
          <span className="hud-eyebrow">pending · human action</span>
          <ul className="pl-pending" aria-label="Extractors needing a human, highest priority first">
            {shown(extraction.pending).map((p) => (
              <li key={p.extractorId} className="pl-pending-row" data-priority={p.priority}>
                <PriorityGlyph priority={p.priority} />
                <span className="pl-pending-name">{p.name}</span>
                <span className="pl-pending-reason">{p.reason}</span>
                <span className="pl-pending-age">{ageText(p.updatedMs, now)}</span>
              </li>
            ))}
            {extraction.read === 'ok' && extraction.pending.items.length === 0 ? <li className="hud-unit">nothing pending</li> : null}
          </ul>
          <FoldButton hidden={hidden(extraction.pending)} noun="pending" onInspect={() => onInspect('pending')} />
          <span className="hud-eyebrow">routing · by type</span>
          <ul className="pl-types" aria-label="Extractors by type">
            {shown(extraction.byType).map((t) => (
              <li key={t.type} className="pl-type-row">
                <span className="pl-type-name">{t.type}</span>
                <Share share={extraction.byType.items[0] ? t.total / extraction.byType.items[0].total : 0} />
                <span className="pl-type-count">{t.enabled}/{t.total}</span>
              </li>
            ))}
          </ul>
          <FoldButton hidden={hidden(extraction.byType)} noun="types" onInspect={() => onInspect('types')} />
          <div className="pl-scan" data-state={extraction.scan.state}>
            <span className="hud-unit">24h volume scan · {extraction.scan.state === 'idle' ? 'not run' : extraction.scan.state === 'running' ? 'measuring 24h volume…' : extraction.scan.state === 'failed' ? '24h volume scan failed' : `${extraction.scan.silent} silent · ${extraction.scan.fallout} falling to OTHER`}</span>
            {extraction.scan.state === 'idle' ? (
              scan.onRun ? <Button variant="outline" size="sm" onPress={scan.onRun}>Run check (~40s)</Button> : <span className="hud-unit">the 24h scan runs from an Analyst or Engineer session</span>
            ) : null}
          </div>
        </Unit>

        <Pipe flow={flowing}>
          <Instrument tag="QD" name="queue depth" value="—" wired={false} />
        </Pipe>

        {/* ---------------------------------------------------- Enrichment */}
        <Unit stage="enrichment" abnormal={false} off>
          <p className="hud-unit">not instrumented · passes through unmeasured</p>
          <ul className="pl-enrichers" aria-label="Enrichers">
            {['GeoDB', 'threat intel', 'asset context', 'identity'].map((name) => (
              <li key={name} className="pl-enricher"><span>{name}</span><Share share={0} /></li>
            ))}
          </ul>
          <span className="hud-eyebrow">proposed reads</span>
          <p className="hud-unit">hit rate per enricher · lookup latency p95 · feed age · unresolved entities</p>
          <p className="hud-unit">raises nothing until a read exists (BACKEND-ASKS)</p>
        </Unit>

        <Pipe flow={flowing}>
          <Instrument tag="LT" name="latency p95" value="—" wired={false} />
        </Pipe>

        {/* ---------------------------------------------------- Data model */}
        <Unit stage="data-model" abnormal={abnormal('data-model')}>
          <ReadNote state={dataModel.read} what="data model" />
          <span className="hud-eyebrow">DDM · {dataModel.declared} declared</span>
          <Gauge value={dataModel.withData} max={dataModel.declared} label="modelled streams with data" />
          <ul className="pl-ddm" aria-label="Data model coverage">
            <li data-priority={dataModel.missing.length ? 'high' : undefined}>
              {dataModel.missing.length ? <PriorityGlyph priority="high" /> : <span className="pl-glyph" aria-hidden="true" />}
              <button type="button" className="pl-linkish" onClick={() => onInspect('ddm-missing')} disabled={dataModel.missing.length === 0}>{dataModel.missing.length} data, no model</button>
            </li>
            <li data-priority={dataModel.empty.length ? 'info' : undefined}>
              {dataModel.empty.length ? <PriorityGlyph priority="info" /> : <span className="pl-glyph" aria-hidden="true" />}
              <button type="button" className="pl-linkish" onClick={() => onInspect('ddm-empty')} disabled={dataModel.empty.length === 0}>{dataModel.empty.length} modelled, no data</button>
            </li>
          </ul>
          <p className="hud-unit">fields and entity kinds live on the Data model page</p>
        </Unit>

        <Pipe flow={flowing}>
          <Instrument tag="SZ" name="on disk" value={formatBytes(storage.totalBytes)} wired />
        </Pipe>

        {/* ---------------------------------------------------- Storage */}
        <Unit stage="storage" abnormal={abnormal('storage')}>
          <ReadNote state={storage.read} what="streams" />
          <span className="hud-eyebrow">{storage.retentionDays === null ? 'retention —' : `${storage.retentionDays}d · default`} · {storage.replication === null ? '—' : `${storage.replication}×`}</span>
          <ul className="pl-disk" aria-label="Largest streams on disk">
            {shown(storage.onDisk).map((r) => (
              <li key={r.stream} className="pl-disk-row">
                <span className="pl-disk-name">{r.stream}</span>
                <Share share={r.share} />
                <span className="pl-disk-bytes">{formatBytes(r.bytes)}</span>
              </li>
            ))}
          </ul>
          <FoldButton hidden={hidden(storage.onDisk)} noun="streams" onInspect={() => onInspect('disk')} />
          <ul className="pl-tiers" aria-label="Retention checks">
            <li data-priority={storage.noRetention.length ? 'high' : undefined}>{storage.noRetention.length ? <PriorityGlyph priority="high" /> : <span className="pl-glyph" aria-hidden="true" />}<span>{storage.noRetention.length} no retention</span></li>
            <li data-priority={storage.drift.length ? 'medium' : undefined}>{storage.drift.length ? <PriorityGlyph priority="medium" /> : <span className="pl-glyph" aria-hidden="true" />}<span>{storage.drift.length} differ from default</span></li>
            <li data-priority={storage.replicated.length ? 'medium' : undefined}>{storage.replicated.length ? <PriorityGlyph priority="medium" /> : <span className="pl-glyph" aria-hidden="true" />}<span>{storage.replicated.length} replicated &gt; 1</span></li>
          </ul>
          <p className="hud-unit">headroom — (CB-32) · event stores on the Storage page</p>
        </Unit>
      </div>

      {/* ---------------------------------------------------- Losses */}
      <div className="pl-losses" role="region" aria-label="Losses">
        <span className="hud-eyebrow">Losses · not reaching storage as useful data</span>
        <ul className="pl-loss-list">
          {model.losses.map((l) => (
            <li key={l.id} className="pl-loss" data-stage={l.stage} data-state={l.state} data-priority={l.state === 'issue' ? l.priority : undefined}>
              {l.state === 'issue' ? <PriorityGlyph priority={l.priority} /> : <span className="pl-glyph" aria-hidden="true" />}
              {link(l, <>{l.label} <b className="pl-loss-count">{l.state === 'idle' ? 'needs scan' : l.state === 'loading' ? '…' : l.state === 'error' ? '—' : l.count}</b></>, `${l.label}: review`)}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
